/**
 * Skills Display Controls — per-resume shape of the rendered Skills section.
 *
 * Storage contract (BINDING, mirrored by the backend — see
 * `features/skills_display_controls.md`): the values live on the resume at
 * `resumejson.skills_settings`. An ABSENT object means "legacy resume" and the
 * global settings (`skills_max_categories`, `skill_group_max_lines`, …) apply
 * exactly as they do today — so the defaults below are what the CONTROLS SHOW,
 * never something written to a resume that has not opted in.
 *
 * Both the editor controls and the live preview read this module so the number
 * shown in the input and the shape drawn on the mini-resume can never disagree.
 */
import type { ResumeJSON, Skill, SkillsSettings } from '../types';

export type SkillsDisplayMode = NonNullable<SkillsSettings['display']>;

export const SKILLS_DISPLAY_DEFAULT: SkillsDisplayMode = 'sections';
export const SKILLS_MAX_SECTIONS_DEFAULT = 4;
export const SKILLS_MAX_LINES_DEFAULT = 1;
/**
 * The trailing "Other" line (high-relevance skills whose group did not make the
 * cut) is ON unless the user turns it off — that is the shape every existing
 * resume already renders, so absent MUST mean true.
 */
export const SKILLS_INCLUDE_OTHER_DEFAULT = true;

/** Input bounds for the two number controls (spec: sections 1–8, lines 1–4). */
export const SKILLS_MAX_SECTIONS_MIN = 1;
export const SKILLS_MAX_SECTIONS_MAX = 8;
export const SKILLS_MAX_LINES_MIN = 1;
export const SKILLS_MAX_LINES_MAX = 4;

/** The four control values with defaults applied (never mutates the resume). */
export function resolveSkillsSettings(
  settings: SkillsSettings | undefined,
): Required<SkillsSettings> {
  return {
    display: settings?.display ?? SKILLS_DISPLAY_DEFAULT,
    max_sections: settings?.max_sections ?? SKILLS_MAX_SECTIONS_DEFAULT,
    max_lines: settings?.max_lines ?? SKILLS_MAX_LINES_DEFAULT,
    include_other: settings?.include_other ?? SKILLS_INCLUDE_OTHER_DEFAULT,
  };
}

/** Clamp a number control to its bounds; a non-numeric entry keeps `fallback`. */
export function clampSkillsNumber(raw: string, min: number, max: number, fallback: number): number {
  const n = parseInt(raw, 10);
  if (isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * Reading order for "Individual best fit": every INCLUDED skill, ordered by
 * skill group (the group's `order`, ties broken by position in
 * `resume.skill_groups`), then by resume order within the group. Skills that
 * are ungrouped — or point at a group that no longer exists — come LAST, in
 * resume order.
 *
 * This is the display order only; per-job the backend also drops the
 * lowest-matching skills to fit the line budget, which the preview (a master
 * view with no job to match against) deliberately does not model.
 */
export function orderSkillsForIndividual(
  resume: Pick<ResumeJSON, 'skills' | 'skill_groups'>,
): Skill[] {
  const skills = (resume.skills || []).filter(s => s?.include !== '0');
  const groups = resume.skill_groups || [];
  // Rank per group id: sort by `order` (missing = 0), ties by declaration order.
  const rank = new Map<string, number>();
  groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => ((a.g.order ?? 0) - (b.g.order ?? 0)) || (a.i - b.i))
    .forEach(({ g }, i) => rank.set(g.id, i));

  return skills
    .map((skill, i) => {
      const gid = skill.group_id;
      const r = gid ? rank.get(gid) : undefined;
      // Ungrouped (and orphaned group_id) sort after every real group.
      return { skill, i, r: r === undefined ? groups.length : r };
    })
    .sort((a, b) => (a.r - b.r) || (a.i - b.i))
    .map(x => x.skill);
}
